import { useEffect, useState } from "react";
import { 
  LineChart, Line, PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell 
} from 'recharts';
import { 
  FiTrendingUp, 
  FiMail, 
  FiEye, 
  FiMousePointer, 
  FiAlertCircle, 
  FiUserMinus ,
  FiInbox
} from 'react-icons/fi';
import { fetchEmailStats } from "../api/contacts";
import { useAuth } from "../context/AuthContext";

// Modern soft color palette
const COLORS = ['#4F46E5', '#3B82F6', '#06B6D4', '#10B981', '#F59E0B'];

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
    <div className="flex items-center gap-3">
      <div className="bg-gray-50 p-2.5 rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-gray-600" />
      </div>
      <div className="flex-1">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-semibold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);
// test

export default function Dashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [emailTimeline, setEmailTimeline] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [bounceComplaintData, setBounceComplaintData] = useState([]);

  useEffect(() => {
    const loadStats = async () => {
      if (!token?.access) return;
      setLoading(true);

      try {
        const data = await fetchEmailStats(token.access);

        // Set all stats for cards
        setStats({
          total_contacts: data.total_contacts,
          total_sent: data.total_sent,
          total_opened: data.total_opened,
          open_rate: `${data.open_rate_percent}%`,
          total_failed: data.total_failed,
          total_pending: data.total_pending,
          bounced: data.bounced_count,
          bounce_rate: `${data.bounce_rate_percent}%`,
          complaints: data.complaint_count,
          complaint_rate: `${data.complaint_rate_percent}%`,
          unsent_contacts: data.total_contacts - data.total_sent
        });

        // LineChart: simple weekly distribution (even split)
        setEmailTimeline([
          { date: "Mon", sent: data.total_sent / 5, opened: data.total_opened / 5 },
          { date: "Tue", sent: data.total_sent / 5, opened: data.total_opened / 5 },
          { date: "Wed", sent: data.total_sent / 5, opened: data.total_opened / 5 },
          { date: "Thu", sent: data.total_sent / 5, opened: data.total_opened / 5 },
          { date: "Fri", sent: data.total_sent / 5, opened: data.total_opened / 5 },
        ]);

        // PieChart: sample categories
        setCategoryData([
          { name: "Lawyers", value: 12 },
          { name: "Dentists", value: 8 },
          { name: "Restaurants", value: 15 },
          { name: "IT Services", value: 10 },
          { name: "Others", value: 5 },
        ]);

        // BarChart: email issues
        setBounceComplaintData([
          { type: "Bounced", value: data.bounced_count },
          { type: "Failed", value: data.total_failed },
          { type: "Pending", value: data.total_pending },
          { type: "Complaints", value: data.complaint_count },
        ]);

      } catch (error) {
        console.error("Failed to load stats:", error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [token]);

  if (loading) return <p className="text-gray-500">Loading dashboard...</p>;

  const cardStats = [
    { icon: FiTrendingUp, label: "Total Contacts", value: stats.total_contacts },
    { icon: FiMail, label: "Total Sent", value: stats.total_sent },
    { icon: FiInbox, label: "Not Sent Yet", value: stats.unsent_contacts }, // ✅ New card
    { icon: FiEye, label: "Total Opened", value: stats.total_opened },
    { icon: FiMousePointer, label: "Open Rate", value: stats.open_rate },
    { icon: FiAlertCircle, label: "Total Failed", value: stats.total_failed },
    { icon: FiAlertCircle, label: "Total Pending", value: stats.total_pending },
    { icon: FiAlertCircle, label: "Bounced", value: `${stats.bounced} (${stats.bounce_rate})` },
    { icon: FiUserMinus, label: "Complaints", value: `${stats.complaints} (${stats.complaint_rate})` },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <h1 className="text-3xl font-semibold text-gray-900 mb-6">Dashboard Overview</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {cardStats.map((stat, idx) => (
          <StatCard key={idx} {...stat} />
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Line Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Emails Sent Over Time</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={emailTimeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#6B7280" fontSize={12} />
              <YAxis stroke="#6B7280" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '6px' }} />
              <Legend />
              <Line type="monotone" dataKey="sent" stroke="#4F46E5" strokeWidth={2} name="Sent" />
              <Line type="monotone" dataKey="opened" stroke="#3B82F6" strokeWidth={2} name="Opened" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contacts by Category</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Issues</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={bounceComplaintData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="type" stroke="#6B7280" fontSize={12} />
            <YAxis stroke="#6B7280" fontSize={12} />
            <Tooltip contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '6px' }} />
            <Legend />
            <Bar dataKey="value" fill="#10B981" name="Count" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
